'use client';
import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import { AsOf, SecuritiesManager } from '@benzinga/securities-manager';
import { SessionContext } from '@benzinga/session-context';
import { useBenzingaEdge } from '@benzinga/edge';
import { useQuoteTrackClick } from '../../utils/quote/useQuoteTracking';
import {
  QuoteDataTable,
  QuoteHeaderRow,
  QuoteSectionRow,
  QuoteTd,
  QuoteTh,
  QuoteThead,
} from './QuoteDataTable';
import { EdgeGateCTAOverlay } from './EdgeRowsGate';
import { formatMoney, formatPercent, percentChange } from './formatters';
import { KPIBox } from './KPIBox';
import { ChartPlotSkeleton, FinancialsSkeleton } from './QuoteSkeletons';
import type { EChartsOption } from 'echarts';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

const GATED_TABLE_CLS =
  '[&_td:nth-child(n+3)]:blur-sm [&_td:nth-child(n+3)]:opacity-60 [&_td:nth-child(n+3)]:select-none ' +
  '[&_th:nth-child(n+3)]:blur-sm [&_th:nth-child(n+3)]:opacity-60';

type FinPeriod = {
  asOf: string;
  totalRevenue: number;
  grossProfit: number | null;
  operatingIncome: number | null;
  netIncome: number | null;
  dilutedEps: number | null;
  freeCashFlow: number | null;
  operatingCashFlow: number | null;
  capitalExpenditure: number | null;
  cashAndCash: number | null;
  totalCurrentAssets: number | null;
  totalAssets: number | null;
  longTermDebt: number | null;
  totalLiabilities: number | null;
  totalEquity: number | null;
};

const DeltaCell: React.FC<{ curr: number; prev: number | undefined }> = ({ curr, prev }) => {
  if (prev == null) return <QuoteTd align="right" muted>—</QuoteTd>;
  const delta = percentChange(curr, prev);
  if (delta === null) return <QuoteTd align="right" muted>—</QuoteTd>;
  const tone = delta >= 0 ? 'text-green-600' : 'text-red-600';
  return (
    <QuoteTd align="right" className={`text-xs ${tone}`}>
      {delta >= 0 ? '+' : ''}
      {delta.toFixed(1)}%
    </QuoteTd>
  );
};

interface FinancialsPageContentProps {
  symbol: string;
}

const FinancialsPageContent: React.FC<FinancialsPageContentProps> = ({ symbol }) => {
  const [dataPeriod, setDataPeriod] = React.useState<'quarterly' | 'annual'>('quarterly');
  const [data, setData] = React.useState<FinPeriod[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [grossMarginTTMFromRatios, setGrossMarginTTMFromRatios] = React.useState<number | null>(null);

  const session = React.useContext(SessionContext);
  const securitiesManager = React.useMemo(() => session.getManager(SecuritiesManager), [session]);
  const trackClick = useQuoteTrackClick();
  const { hasEdgeSubscription } = useBenzingaEdge();
  const isGated = !hasEdgeSubscription;

  React.useEffect(() => {
    setData([]);
    setGrossMarginTTMFromRatios(null);
    setLoading(true);

    const apiPeriod = dataPeriod === 'quarterly' ? '3M' : '12M';
    const asOf = (
      dataPeriod === 'quarterly' ? DateTime.local().minus({ months: 26 }) : DateTime.local().minus({ years: 9 })
    ).toFormat('yyyy-MM-dd') as AsOf;

    async function load() {
      try {
        const [primary, quarterly] = await Promise.all([
          securitiesManager.getFundamentals({ asOf, period: apiPeriod, symbols: symbol }),
          dataPeriod === 'annual'
            ? securitiesManager.getFundamentals({ asOf, period: '3M', symbols: symbol })
            : Promise.resolve({ ok: undefined } as Awaited<ReturnType<typeof securitiesManager.getFundamentals>>),
        ]);
        if (!primary.ok) return;
        const records = primary.ok[0]?.financials ?? [];
        const earningReports = primary.ok[0]?.earningReports ?? [];
        const ttmGrossMargin = primary.ok[0]?.operationRatios?.[0]?.grossMargin;
        setGrossMarginTTMFromRatios(ttmGrossMargin != null ? ttmGrossMargin * 100 : null);
        const epsMap = new Map<string, number>();
        for (const er of earningReports) {
          if (er.id?.asOf && er.dilutedEps != null) {
            epsMap.set(er.id.asOf, er.dilutedEps);
          }
        }
        const balanceSheetByAsOf = new Map<string, NonNullable<typeof records[number]['balanceSheet']>>();
        if (quarterly.ok) {
          for (const q of quarterly.ok[0]?.financials ?? []) {
            if (q.asOf && q.balanceSheet) balanceSheetByAsOf.set(q.asOf, q.balanceSheet);
          }
        }
        const mapped: FinPeriod[] = records
          .filter(r => r.asOf && r.incomeStatement?.totalRevenue != null)
          .map(r => {
            const bs = balanceSheetByAsOf.get(r.asOf) ?? r.balanceSheet;
            return {
              asOf: r.asOf,
              totalRevenue: r.incomeStatement?.totalRevenue ?? 0,
              grossProfit: r.incomeStatement?.grossProfit ?? null,
              operatingIncome: r.incomeStatement?.operatingIncome ?? null,
              netIncome: r.incomeStatement?.netIncome ?? null,
              dilutedEps: epsMap.get(r.asOf) ?? null,
              freeCashFlow: r.cashFlowStatement?.freeCashFlow ?? null,
              operatingCashFlow: r.cashFlowStatement?.operatingCashFlow ?? null,
              capitalExpenditure: r.cashFlowStatement?.capitalExpenditure ?? null,
              cashAndCash: bs?.cashAndCashEquivalents ?? null,
              totalCurrentAssets: bs?.currentAssets ?? null,
              totalAssets: bs?.totalAssets ?? null,
              longTermDebt: bs?.longTermDebt ?? null,
              totalLiabilities: bs?.totalLiabilities ?? null,
              totalEquity: bs?.totalEquity ?? null,
            };
          })
          .sort((a, b) => a.asOf.localeCompare(b.asOf));
        setData(mapped);
      } finally {
        setLoading(false);
      }
    }

    load().catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securitiesManager, symbol, dataPeriod]);

  const displayPeriods = useMemo(() => {
    if (dataPeriod !== 'annual' || data.length === 0) {
      return data.slice(-8).slice().reverse();
    }
    const mostRecentMonth = data[data.length - 1].asOf.slice(5, 7);
    return data.filter(d => d.asOf.slice(5, 7) === mostRecentMonth).slice(-8).reverse();
  }, [data, dataPeriod]);

  const labels = useMemo(
    () =>
      displayPeriods.map(d => {
        const dt = DateTime.fromISO(d.asOf);
        return dataPeriod === 'annual' ? dt.toFormat('yyyy') : `${dt.toFormat('LLL')} '${dt.toFormat('yy')}`;
      }),
    [displayPeriods, dataPeriod],
  );

  const ttmSlice = useMemo(() => data.slice(-4), [data]);
  const priorSlice = useMemo(() => data.slice(-8, -4), [data]);

  const ttmRevenue = useMemo(() => ttmSlice.reduce((s, d) => s + d.totalRevenue, 0), [ttmSlice]);
  const priorRevenue = useMemo(() => priorSlice.reduce((s, d) => s + d.totalRevenue, 0), [priorSlice]);
  const revenueYoY = useMemo(() => percentChange(ttmRevenue, priorRevenue), [ttmRevenue, priorRevenue]);

  const ttmGrossProfit = useMemo(() => {
    const nonNull = ttmSlice.filter(d => d.grossProfit !== null);
    return nonNull.length > 0 ? nonNull.reduce((s, d) => s + (d.grossProfit as number), 0) : null;
  }, [ttmSlice]);
  const grossMarginPct =
    grossMarginTTMFromRatios ?? (ttmRevenue && ttmGrossProfit !== null ? (ttmGrossProfit / ttmRevenue) * 100 : null);

  const ttmOperatingIncome = useMemo(() => {
    const nonNull = ttmSlice.filter(d => d.operatingIncome !== null);
    return nonNull.length > 0 ? nonNull.reduce((s, d) => s + (d.operatingIncome as number), 0) : null;
  }, [ttmSlice]);
  const opMarginPct = ttmOperatingIncome !== null && ttmRevenue ? (ttmOperatingIncome / ttmRevenue) * 100 : null;

  const ttmNetIncome = useMemo(() => {
    const nonNull = ttmSlice.filter(d => d.netIncome !== null);
    return nonNull.length > 0 ? nonNull.reduce((s, d) => s + (d.netIncome as number), 0) : null;
  }, [ttmSlice]);
  const netMarginPct = ttmNetIncome !== null && ttmRevenue ? (ttmNetIncome / ttmRevenue) * 100 : null;

  const ttmFCF = useMemo(() => {
    const nonNull = ttmSlice.filter(d => d.freeCashFlow !== null);
    return nonNull.length > 0 ? nonNull.reduce((s, d) => s + (d.freeCashFlow as number), 0) : null;
  }, [ttmSlice]);
  const mrlLTDebt = data.length > 0 ? data[data.length - 1].longTermDebt : null;

  const revenueChartOpt = useMemo(
    () =>
      ({
        grid: { left: 60, right: 16, top: 8, bottom: 56 },
        legend: { data: ['Revenue', 'Net Income'], bottom: 0, textStyle: { fontSize: 10 } },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10 } },
        yAxis: {
          type: 'value',
          axisLabel: {
            fontSize: 10,
            formatter: (value: number) => {
              if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(0)}B`;
              if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
              return `$${value}`;
            },
          },
          splitLine: { lineStyle: { color: '#f0f0f0' } },
        },
        series: [
          {
            name: 'Revenue',
            type: 'bar',
            data: displayPeriods.map((period, index) => ({
              value: period.totalRevenue,
              itemStyle: isGated && index > 0 ? { opacity: 0.3 } : undefined,
            })),
            itemStyle: { color: '#30BF60' },
            tooltip: { valueFormatter: (value: number) => formatMoney(value) },
          },
          {
            name: 'Net Income',
            type: 'bar',
            data: displayPeriods.map((period, index) => ({
              value: period.netIncome,
              itemStyle: isGated && index > 0 ? { opacity: 0.3 } : undefined,
            })),
            itemStyle: { color: '#2f80ed' },
            tooltip: { valueFormatter: (value: number) => formatMoney(value) },
          },
        ],
      }) as EChartsOption,
    [displayPeriods, labels, isGated],
  );

  const grossMarginChartOpt = useMemo(
    () =>
      ({
        grid: { left: 52, right: 16, top: 8, bottom: 36 },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10 } },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: (value: number) => `${value}%`, fontSize: 10 },
          splitLine: { lineStyle: { color: '#f0f0f0' } },
        },
        series: [
          {
            name: 'Gross Margin %',
            type: 'line',
            data: displayPeriods.map((period, index) => {
              const value =
                period.grossProfit !== null && period.totalRevenue
                  ? Math.round((period.grossProfit / period.totalRevenue) * 1000) / 10
                  : null;
              return isGated && index > 0 ? { value, itemStyle: { opacity: 0.3 } } : value;
            }),
            lineStyle: { color: '#f2994a', width: 2, opacity: isGated ? 0.45 : 1 },
            itemStyle: { color: '#f2994a' },
            areaStyle: { color: 'rgba(242,153,74,0.12)', opacity: isGated ? 0.5 : 1 },
            smooth: true,
            tooltip: { valueFormatter: (value: number) => `${value.toFixed(1)}%` },
          },
        ],
      }) as EChartsOption,
    [displayPeriods, labels, isGated],
  );

  const colCount = displayPeriods.length + 1;

  if (loading) {
    return <FinancialsSkeleton />;
  }

  if (data.length === 0) {
    return <div className="py-12 text-center text-sm text-gray-400">No financial data available.</div>;
  }

  return (
    <div className="flex flex-col gap-y-8">
      <div className="flex justify-end gap-2">
        {(['quarterly', 'annual'] as const).map(p => (
          <button
            key={p}
            onClick={() => {
              setDataPeriod(p);
              trackClick('financials_period_toggle');
            }}
            className={`rounded px-3 py-1 text-xs font-semibold capitalize transition-colors ${
              dataPeriod === p ? 'bg-bzblue-1000 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {dataPeriod === 'quarterly' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <KPIBox
            label="TTM Revenue"
            value={formatMoney(ttmRevenue)}
            sub={revenueYoY !== null ? `${revenueYoY >= 0 ? '+' : ''}${revenueYoY.toFixed(1)}% YoY` : undefined}
            subTone={revenueYoY !== null ? (revenueYoY >= 0 ? 'positive' : 'negative') : undefined}
          />
          <KPIBox label="Gross Margin (TTM)" value={formatPercent(grossMarginPct)} />
          <KPIBox
            label="Operating Income (TTM)"
            value={formatMoney(ttmOperatingIncome)}
            sub={opMarginPct !== null ? `${formatPercent(opMarginPct)} margin` : undefined}
          />
          <KPIBox
            label="Net Income (TTM)"
            value={formatMoney(ttmNetIncome)}
            sub={netMarginPct !== null ? `${formatPercent(netMarginPct)} margin` : undefined}
          />
          <KPIBox label="Free Cash Flow (TTM)" value={formatMoney(ttmFCF)} />
          <KPIBox label="LT Debt (MRQ)" value={formatMoney(mrlLTDebt)} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded border border-[#e9ecef] bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Revenue &amp; Net Income</p>
          <React.Suspense fallback={<ChartPlotSkeleton label="Loading revenue chart" />}>
            <ReactECharts
              key={`revenue-chart-${dataPeriod}`}
              notMerge={true}
              lazyUpdate={true}
              option={revenueChartOpt}
              style={{ height: 220 }}
            />
          </React.Suspense>
        </div>
        <div className="rounded border border-[#e9ecef] bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Gross Margin %</p>
          <React.Suspense fallback={<ChartPlotSkeleton label="Loading margin chart" />}>
            <ReactECharts
              key={`gross-margin-chart-${dataPeriod}`}
              notMerge={true}
              lazyUpdate={true}
              option={grossMarginChartOpt}
              style={{ height: 220 }}
            />
          </React.Suspense>
        </div>
      </div>

      <div className="relative">
        <QuoteDataTable tableClassName={isGated ? GATED_TABLE_CLS : undefined}>
          <QuoteThead>
            <QuoteHeaderRow>
              <QuoteTh>Income Statement</QuoteTh>
              {labels.map((label, idx) => (
                <QuoteTh key={`income-statement-${label}-${idx}`} align="right">
                  {label}
                </QuoteTh>
              ))}
            </QuoteHeaderRow>
          </QuoteThead>
          <tbody>
            <QuoteSectionRow colSpan={colCount}>Revenue</QuoteSectionRow>
            <tr className="border-b border-gray-100">
              <QuoteTd>Total Revenue</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`total-revenue-${d.asOf}-${idx}`} bold>
                  {formatMoney(d.totalRevenue)}
                </QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-200 bg-gray-50">
              <QuoteTd muted>
                Period Change
              </QuoteTd>
              {displayPeriods.map((d, i) => (
                <DeltaCell
                  key={`period-change-${d.asOf}-${i}`}
                  curr={d.totalRevenue}
                  prev={displayPeriods[i + 1]?.totalRevenue}
                />
              ))}
            </tr>

            <QuoteSectionRow colSpan={colCount}>Profitability</QuoteSectionRow>
            <tr className="border-b border-gray-100">
              <QuoteTd>Gross Profit</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`gross-profit-${d.asOf}-${idx}`}>{formatMoney(d.grossProfit)}</QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-100 bg-gray-50">
              <QuoteTd muted>
                Gross Margin %
              </QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd key={`gross-margin-${d.asOf}-${idx}`} align="right" className="text-gray-600">
                  {d.grossProfit !== null && d.totalRevenue ? formatPercent((d.grossProfit / d.totalRevenue) * 100) : '—'}
                </QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-100">
              <QuoteTd>Operating Income</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`operating-income-${d.asOf}-${idx}`}>{formatMoney(d.operatingIncome)}</QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-100 bg-gray-50">
              <QuoteTd muted>
                Operating Margin %
              </QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd key={`operating-margin-${d.asOf}-${idx}`} align="right" className="text-gray-600">
                  {d.operatingIncome !== null && d.totalRevenue ? formatPercent((d.operatingIncome / d.totalRevenue) * 100) : '—'}
                </QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-100">
              <QuoteTd>Net Income</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`net-income-${d.asOf}-${idx}`} bold>
                  {formatMoney(d.netIncome)}
                </QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-100 bg-gray-50">
              <QuoteTd muted>
                Net Margin %
              </QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd key={`net-margin-${d.asOf}-${idx}`} align="right" className="text-gray-600">
                  {d.netIncome !== null && d.totalRevenue ? formatPercent((d.netIncome / d.totalRevenue) * 100) : '—'}
                </QuoteTd>
              ))}
            </tr>
            <tr>
              <QuoteTd bold>
                EPS (Diluted)
              </QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`eps-diluted-${d.asOf}-${idx}`} bold>
                  {d.dilutedEps != null ? `$${d.dilutedEps.toFixed(2)}` : '—'}
                </QuoteTd>
              ))}
            </tr>
          </tbody>
        </QuoteDataTable>
        {isGated && displayPeriods.length > 1 && <EdgeGateCTAOverlay type="financials" />}
      </div>

      <QuoteDataTable tableClassName={isGated ? GATED_TABLE_CLS : undefined}>
          <QuoteThead>
            <QuoteHeaderRow>
              <QuoteTh>Cash Flow</QuoteTh>
              {labels.map((label, idx) => (
                <QuoteTh key={`cash-flow-${label}-${idx}`} align="right">
                  {label}
                </QuoteTh>
              ))}
            </QuoteHeaderRow>
          </QuoteThead>
          <tbody>
            <tr className="border-b border-gray-100">
              <QuoteTd>Cash from Operations</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`cash-from-operations-${d.asOf}-${idx}`}>{formatMoney(d.operatingCashFlow)}</QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-100 bg-gray-50">
              <QuoteTd>Capital Expenditures</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`capital-expenditures-${d.asOf}-${idx}`}>{formatMoney(d.capitalExpenditure)}</QuoteTd>
              ))}
            </tr>
            <tr>
              <QuoteTd bold>
                Free Cash Flow
              </QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`free-cash-flow-${d.asOf}-${idx}`} bold>
                  {formatMoney(d.freeCashFlow)}
                </QuoteTd>
              ))}
            </tr>
          </tbody>
      </QuoteDataTable>

      <QuoteDataTable tableClassName={isGated ? GATED_TABLE_CLS : undefined}>
          <QuoteThead>
            <QuoteHeaderRow>
              <QuoteTh>Balance Sheet</QuoteTh>
              {labels.map((label, idx) => (
                <QuoteTh key={`balance-sheet-${label}-${idx}`} align="right">
                  {label}
                </QuoteTh>
              ))}
            </QuoteHeaderRow>
          </QuoteThead>
          <tbody>
            <QuoteSectionRow colSpan={colCount}>Assets</QuoteSectionRow>
            <tr className="border-b border-gray-100">
              <QuoteTd>Cash &amp; Equivalents</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`cash-and-cash-equivalents-${d.asOf}-${idx}`}>{formatMoney(d.cashAndCash)}</QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-100 bg-gray-50">
              <QuoteTd>Total Current Assets</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`total-current-assets-${d.asOf}-${idx}`}>{formatMoney(d.totalCurrentAssets)}</QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-200">
              <QuoteTd bold>
                Total Assets
              </QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`total-assets-${d.asOf}-${idx}`} bold>
                  {formatMoney(d.totalAssets)}
                </QuoteTd>
              ))}
            </tr>

            <QuoteSectionRow colSpan={colCount}>Liabilities & Equity</QuoteSectionRow>
            <tr className="border-b border-gray-100">
              <QuoteTd>Long-term Debt</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`long-term-debt-${d.asOf}-${idx}`}>{formatMoney(d.longTermDebt)}</QuoteTd>
              ))}
            </tr>
            <tr className="border-b border-gray-100 bg-gray-50">
              <QuoteTd>Total Liabilities</QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`total-liabilities-${d.asOf}-${idx}`}>{formatMoney(d.totalLiabilities)}</QuoteTd>
              ))}
            </tr>
            <tr>
              <QuoteTd bold>
                Total Equity
              </QuoteTd>
              {displayPeriods.map((d, idx) => (
                <QuoteTd align="right" key={`total-equity-${d.asOf}-${idx}`} bold>
                  {formatMoney(d.totalEquity)}
                </QuoteTd>
              ))}
            </tr>
          </tbody>
      </QuoteDataTable>

    </div>
  );
};

export default FinancialsPageContent;
